import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import {
    LayoutDashboard,
    Package,
    ChevronDown,
    ChevronLeft,
    Users,
    Shield,
    Menu,
    Layers3,
    ShieldCheck,
    X,
    ExternalLink,
    Building2,
    resolveIcon,
    SquareMenu,
    Compass,
    List
} from "@/components/icons/index";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import useAuthUser from "@/hooks/useAuthUser";
import useImportModuleLabels from "@/hooks/useImportModuleLabels";
import { getSideBarNavigationList } from "@/redux/features/sidebarNavigation/sidebarNavigationThunk";
import SidebarSkeleton from "../skeleton/SidebarSkeleton";
import { prefetchRoute } from "@/utils/routePrefetch";
import { ensureActiveSession } from "@/utility/authSession";

// ─── Navigation style tokens ──────────────────────────────────────────────────
// Light mode: brighter menu text. Dark mode: softer / less white for less glare.
const NAV_STYLES = {
    activeMain:
        "bg-gradient-to-r from-blue-600/35 via-blue-500/22 to-teal-500/15 text-white font-semibold shadow-[inset_0_0_0_1px_rgba(96,165,250,0.55)] dark:from-blue-600/25 dark:via-blue-500/15 dark:to-teal-500/10 dark:text-slate-100 dark:shadow-[inset_0_0_0_1px_rgba(59,130,246,0.35)]",
    activeSub:
        "bg-white/[0.12] text-white font-medium shadow-[inset_3px_0_0_0_rgba(96,165,250,1)] dark:bg-white/[0.08] dark:text-slate-200 dark:shadow-[inset_3px_0_0_0_rgba(59,130,246,0.9)]",
    hover:
        "text-[#E8EAED] hover:bg-white/[0.1] hover:text-white dark:text-[#9CA3AF] dark:hover:bg-white/[0.06] dark:hover:text-slate-200 active:scale-[0.98]",
    hoverSub:
        "text-[#B4BCC8] hover:bg-white/[0.07] hover:text-[#E8EAED] dark:text-[#8B939E] dark:hover:bg-white/[0.05] dark:hover:text-slate-300 active:scale-[0.98]",
    dot: "before:content-[''] before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-6 before:w-[3px] before:rounded-r-full before:bg-gradient-to-b before:from-blue-400 before:via-blue-300 before:to-teal-300 before:shadow-[0_0_10px_rgba(96,165,250,0.65)] dark:before:from-blue-500 dark:before:via-blue-400 dark:before:to-teal-400 dark:before:shadow-[0_0_8px_rgba(59,130,246,0.5)]",
    mainLabel:
        "font-['Space_Grotesk'] font-semibold text-[14.5px] leading-normal tracking-[-0.01em]",
    midLabel:
        "font-['Space_Grotesk'] font-medium text-[13.5px] leading-snug tracking-[-0.005em]",
    subLabel:
        "font-['Inter'] font-normal text-[13px] leading-snug tracking-[0.02em]",
};

const normalizePathname = (pathname = "") => {
    const p = pathname.split("?")[0].split("#")[0].replace(/\/+$/, "");
    return p || "/";
};

/** Longest-prefix match so /booking/cancel highlights cancel, not booking. */
const resolveActiveNavId = (pathname, navIds = []) => {
    const path = normalizePathname(pathname);
    let bestId = null;
    let bestLen = 0;

    navIds.forEach((id) => {
        if (!id || id.includes("://")) return;
        const target = `/${id}`;
        const matches = path === target || path.startsWith(`${target}/`);
        if (matches && target.length > bestLen) {
            bestLen = target.length;
            bestId = id;
        }
    });

    return bestId;
};

const isNavIdActive = (pathname, itemId, activeNavId) =>
    Boolean(itemId && activeNavId === itemId);

const openInNewTab = (url, { external = false } = {}) => {
    // In-app destinations need a live session; external links (Cargorates, docs)
    // are unaffected by the app's own auth state.
    if (!external && !ensureActiveSession()) return;
    const href = external
        ? url
        : `${window.location.origin}${url.startsWith("/") ? url : `/${url}`}`;
    window.open(href, "_blank", "noopener,noreferrer");
};

const CARGORATES_SIGN_IN_URLS = {
    1: "https://app.cargorates.ai/WebPortal/SignIn.aspx?b=UC9KWFNIYTFRb0dIM0cyMzNaWHJnQT09&d=R2VUWVMxWmFQRUZObytJSVp4Y016QT09",
    2: "https://app.cargorates.ai/WebPortal/SignIn.aspx?b=Uzcvc3R0MXhaR3FCcWxVZlFqekV1dz09&d=YWl3dkJKckh1SVR1dnEzUDlSa1ZCZz09",
    3: "https://app.cargorates.ai/WebPortal/SignIn.aspx?b=UC9KWFNIYTFRb0dIM0cyMzNaWHJnQT09&d=R2VUWVMxWmFQRUZObytJSVp4Y016QT09",
};

const DEFAULT_CARGORATES_SIGN_IN_URL = CARGORATES_SIGN_IN_URLS[1];

const buildCargoratesSignInUrl = (companyId) => {
    const id = Number(companyId);
    if (Number.isFinite(id) && CARGORATES_SIGN_IN_URLS[id]) {
        return CARGORATES_SIGN_IN_URLS[id];
    }
    return DEFAULT_CARGORATES_SIGN_IN_URL;
};

const useNavPressFeedback = () => {
    const [pressingId, setPressingId] = useState(null);
    const onPressStart = (id) => setPressingId(id);
    const onPressEnd = () => setPressingId(null);
    const isPressing = (id) => pressingId === id;
    return { onPressStart, onPressEnd, isPressing };
};

// ─── Open in new tab (hover affordance) ───────────────────────────────────────
const OpenInNewTabButton = ({ href, external, label, className = "" }) => (
    <button
        type="button"
        title={`Open ${label} in new tab`}
        aria-label={`Open ${label} in new tab`}
        onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            openInNewTab(href, { external });
        }}
        className={`flex-shrink-0 p-1 rounded-md opacity-0 pointer-events-none
      group-hover:opacity-100 group-hover:pointer-events-auto
      text-slate-300 hover:text-white hover:bg-white/15
      dark:text-slate-500 dark:hover:text-slate-200 dark:hover:bg-white/10
      transition-all duration-200 ${className}`}
    >
        <ExternalLink className="w-3 h-3" strokeWidth={2} />
    </button>
);

// ─── NavIconGlyph ─────────────────────────────────────────────────────────────
const NavIconGlyph = ({ icon, className, strokeWidth }) => {
    if (!icon) return null;
    if (typeof icon === "string") {
        return (
            <img
                src={icon}
                alt=""
                className={`${className} object-contain flex-shrink-0`}
            />
        );
    }
    const Icon = icon;
    return <Icon className={className} strokeWidth={strokeWidth} />;
};

// ─── NavItem (main level) ─────────────────────────────────────────────────────
const navLinkBase =
    "relative w-full flex items-center no-underline rounded-lg transition-all duration-200 ease-out";

const NavItem = React.memo(
    ({ t,
        id,
        label,
        icon,
        activeNavId,
        setPendingNavId,
        pathname,
        externalLink,
        to,
        activePath,
        activeExact = false,
        isCollapsed = false,
        onNavigate,
        pressFeedback,
    }) => {

        const navTo = to || (id === "admin" ? "/admin/" : `/${id}`);
        const normalizedPath = normalizePathname(pathname);
        const normalizedActivePath = normalizePathname(activePath || navTo);
        const isRouteActive =
            normalizedPath === normalizedActivePath ||
            (!activeExact && normalizedPath.startsWith(`${normalizedActivePath}/`));
        const isActive =
            isNavIdActive(pathname, id, activeNavId) ||
            (!externalLink && isRouteActive);
        const pressing = pressFeedback?.isPressing(id);

        const linkClassName = `${navLinkBase} ${isCollapsed ? "justify-center px-2" : "gap-3 px-3"
            } py-2.5 flex-1 min-w-0 ${pressing ? "scale-[0.98] opacity-90" : ""} ${isActive ? `${NAV_STYLES.activeMain} ${NAV_STYLES.dot}` : NAV_STYLES.hover
            }`;

        const handlePressStart = () => pressFeedback?.onPressStart(id);

        const handleExternalClick = (e) => {
            e.preventDefault();
            onNavigate?.();
            openInNewTab(externalLink, { external: true });
            pressFeedback?.onPressEnd();
        };

        const handleInternalClick = (event) => {
            if (!ensureActiveSession()) {
                event.preventDefault();
                pressFeedback?.onPressEnd();
                return;
            }
            setPendingNavId?.(id);
            onNavigate?.();
            pressFeedback?.onPressEnd();
        };

        const linkContent = (
            <>
                <NavIconGlyph
                    icon={icon}
                    className={`w-[18px] h-[18px] flex-shrink-0 transition-colors duration-200 ${isActive
                        ? "text-blue-300 dark:text-blue-300"
                        : "text-slate-400 dark:text-slate-500 group-hover:text-slate-200 dark:group-hover:text-slate-300"
                        }`}
                    strokeWidth={isActive ? 2 : 1.75}
                />
                {!isCollapsed && (
                    <span className={`${NAV_STYLES.mainLabel} text-left flex-1 truncate`}>
                        {t(`${label}`)}
                    </span>
                )}
                {!isCollapsed && externalLink && (
                    <ExternalLink
                        className="w-3 h-3 ml-auto opacity-40 flex-shrink-0"
                        strokeWidth={2}
                    />
                )}
            </>
        );

        return (
            <div
                className={`relative group flex items-center min-w-0 ${isCollapsed ? "" : "pr-1"}`}
            >
                {externalLink ? (
                    <a
                        href={externalLink}
                        title={isCollapsed ? t(`${label}`) : undefined}
                        rel="noopener noreferrer"
                        data-testid={`nav-${id}`}
                        className={linkClassName}
                        onPointerDown={handlePressStart}
                        onClick={handleExternalClick}
                    >
                        {linkContent}
                    </a>
                ) : (
                    <Link
                        to={navTo}
                        title={isCollapsed ? t(`${label}`) : undefined}
                        data-testid={`nav-${id}`}
                        className={linkClassName}
                        onPointerDown={() => {
                            pressFeedback?.onPressStart(id);
                        }}
                        onMouseEnter={() => prefetchRoute(navTo)}
                        onFocus={() => prefetchRoute(navTo)}
                        onClick={handleInternalClick}
                    >
                        {linkContent}
                    </Link>
                )}
                {!isCollapsed && !externalLink && (
                    <OpenInNewTabButton href={navTo} label={t(`${label}`)} />
                )}
                {isCollapsed && (
                    <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2.5 px-2.5 py-1.5 bg-[#0A0A0A] text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 shadow-xl hidden lg:block">
                        {t(`${label}`)}
                        <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-[#0A0A0A]" />
                    </div>
                )}
            </div>
        );
    },
);

// ─── Disabled placeholder for nodes with no real URL yet (URL === "#") ──────
const DisabledLeafItem = ({ label, indent = "pl-9" }) => (
    <div
        className={`relative flex items-center gap-2 ${indent} pr-2 py-[7px] text-white/25 cursor-not-allowed select-none`}
        title="No route configured for this item yet"
    >
        <span className="w-1.5 h-1.5 rounded-full bg-white/15 flex-shrink-0" />
        <span className={`${NAV_STYLES.subLabel} min-w-0 flex-1 truncate`} title={label}>{label}</span>
    </div>
);

const DisabledNavItem = ({ label, icon, isCollapsed }) => (
    <div
        className={`relative w-full flex items-center ${isCollapsed ? "justify-center px-2" : "gap-3 px-3"
            } py-2.5 rounded-lg text-white/25 cursor-not-allowed select-none`}
        title="No route configured for this item yet"
    >
        <NavIconGlyph
            icon={icon}
            className="w-[18px] h-[18px] flex-shrink-0"
            strokeWidth={1.8}
        />
        {!isCollapsed && <span className={`${NAV_STYLES.mainLabel} flex-1 truncate`}>{label}</span>}
    </div>
);

// ─── ParentNavBtn (top-level parent) ─────────────────────────────────────────
const ParentNavBtn = ({
    t,
    icon,
    label,
    isActive,
    isOpen,
    isCollapsed,
    tooltip,
    onClick,
    testId,
}) => (
    <div className="relative group">
        <button
            onClick={onClick}
            data-testid={testId}
            className={`relative w-full flex items-center ${isCollapsed ? "justify-center px-2" : "justify-between px-3"
                } py-2.5 rounded-lg transition-all duration-150 ${isActive
                    ? `${NAV_STYLES.activeMain} ${NAV_STYLES.dot}`
                    : NAV_STYLES.hover
                }`}
        >
            <div className={`flex items-center ${isCollapsed ? "" : "gap-3"}`}>
                <NavIconGlyph
                    icon={icon}
                    className={`w-[18px] h-[18px] flex-shrink-0 transition-colors duration-200 ${isActive
                        ? "text-blue-300 dark:text-blue-300"
                        : "text-slate-400 dark:text-slate-500 group-hover:text-slate-200 dark:group-hover:text-slate-300"
                        }`}
                    strokeWidth={isActive ? 2 : 1.75}
                />
                {!isCollapsed && <span className={NAV_STYLES.mainLabel}>{t(`${label}`)}</span>}
            </div>
            {!isCollapsed && (
                <ChevronDown
                    className={`w-3.5 h-3.5 transition-transform duration-200 opacity-80 ${isOpen ? "rotate-0" : "-rotate-90"
                        }`}
                    strokeWidth={2.5}
                />
            )}
        </button>
        {isCollapsed && (
            <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2.5 px-2.5 py-1.5 bg-[#0A0A0A] text-white text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 shadow-xl hidden lg:block">
                {tooltip || t(`${label}`)}
                <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-[#0A0A0A]" />
            </div>
        )}
    </div>
);

// ─── TreeMenu (outer vertical line) ──────────────────────────────────────────
// `hideLine` — when true the outer guide-line is suppressed so it doesn't
// visually overlap with an inner TreeParent line that's already visible.
const TreeMenu = ({ children, showLine = true, hideLine = false }) => (
    <div className="relative mt-1 mb-1">
        {showLine && !hideLine && (
            <span
                aria-hidden
                className="absolute top-0 bottom-1 left-[25px] w-px bg-white/40 z-10 pointer-events-none"
            />
        )}
        {children}
    </div>
);

// ─── TreeItem (submenu leaf) ─────────────────────────────────────────────────
const TreeItem = ({
    t,
    id,
    label,
    activeNavId,
    setPendingNavId,
    pathname,
    onNavigate,
    testId,
    indent = "pl-9",
    pressFeedback,
    externalUrl,
}) => {
    const isActive = isNavIdActive(pathname, id, activeNavId);
    const pressing = pressFeedback?.isPressing(id);

    const navTo = id ? `/${id}` : null;

    const handleExternalClick = (e) => {
        e.preventDefault();

        onNavigate?.();

        openInNewTab(externalUrl, { external: true });

        pressFeedback?.onPressEnd();
    };

    if (externalUrl) {
        return (
            <div className="relative group flex items-center pr-1">
                <a
                    href={externalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onPointerDown={() => pressFeedback?.onPressStart(label)}
                    onClick={handleExternalClick}
                    data-testid={testId || `nav-external-${label}`}
                    className={`relative flex flex-1 min-w-0 items-center gap-2 no-underline ${indent} pr-2 py-[7px] rounded-lg transition-all duration-200 ${pressing ? "scale-[0.99] opacity-90" : ""
                        } ${NAV_STYLES.hoverSub}`}
                >
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 bg-white/45 dark:bg-white/25" />

                    <span
                        className={`${NAV_STYLES.subLabel} min-w-0 flex-1 truncate`}
                        title={t(`${label}`)}
                    >
                        {t(`${label}`)}
                    </span>

                    <ExternalLink
                        className="w-3 h-3 ml-auto opacity-40 flex-shrink-0"
                        strokeWidth={2}
                    />
                </a>
            </div>
        );
    }

    return (
        <div className="relative group">
            <Link
                to={navTo}
                onPointerDown={() => {
                    pressFeedback?.onPressStart(id);
                }}
                onMouseEnter={() => prefetchRoute(navTo)}
                onFocus={() => prefetchRoute(navTo)}
                onClick={(event) => {
                    if (!ensureActiveSession()) {
                        event.preventDefault();
                        pressFeedback?.onPressEnd();
                        return;
                    }
                    setPendingNavId?.(id);
                    onNavigate?.();
                    pressFeedback?.onPressEnd();
                }}
                data-testid={testId || `nav-${id.replace("/", "-")}`}
                className={`relative flex w-full min-w-0 items-center gap-2 no-underline ${indent} pr-5 py-[7px] rounded-lg transition-all duration-200 ${pressing ? "scale-[0.99] opacity-90" : ""
                    } ${isActive ? NAV_STYLES.activeSub : NAV_STYLES.hoverSub}`}
            >
                <span
                    className={`w-1.5 h-1.5 rounded-full flex-shrink-0 transition-colors duration-200 ${isActive
                        ? "bg-blue-300 shadow-[0_0_6px_rgba(96,165,250,0.9)] dark:bg-blue-400 dark:shadow-[0_0_6px_rgba(59,130,246,0.8)]"
                        : "bg-white/35 dark:bg-white/20"
                        }`}
                    aria-hidden
                />

                <span
                    className={`${NAV_STYLES.subLabel} min-w-0 flex-1 truncate`}
                    title={t(`${label}`)}
                >
                    {t(`${label}`)}
                </span>
            </Link>

            <OpenInNewTabButton
                href={navTo}
                label={t(`${label}`)}
                className="absolute right-0.5 top-1/2 -translate-y-1/2"
            />
        </div>
    );
};

// ─── TreeParent (submenu parent, e.g. Export/Import) ─────────────────────────
const TreeParent = ({
    t,
    icon: Icon,
    label,
    isActive,
    isOpen,
    onClick,
    children,
    testId,
}) => (
    <div className="relative">
        <button
            onClick={onClick}
            data-testid={testId}
            className={`relative w-full flex items-center justify-between pl-9 pr-3 py-1.5 mb-1.5 rounded-lg transition-all duration-150 ${isActive ? NAV_STYLES.activeSub : NAV_STYLES.hover
                }`}
        >
            <div className="flex items-center gap-2">
                <span
                    className={`text-[10px] flex-shrink-0 ${isActive
                        ? "text-blue-300 dark:text-blue-400"
                        : "text-white/50 dark:text-white/20"
                        }`}
                >
                    —
                </span>
                <span className={NAV_STYLES.midLabel}>
                    {t(`${label}`)}
                </span>
            </div>
            <ChevronDown
                className={`w-3 h-3 opacity-80 transition-transform duration-200 ${isOpen ? "rotate-0" : "-rotate-90"
                    }`}
                strokeWidth={2.5}
            />
        </button>

        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
                    className="overflow-hidden"
                >
                    <div className="relative">
                        {/* Inner vertical guide line for sub-navigation level */}
                        <span
                            aria-hidden
                            className="absolute top-0 bottom-1 left-[48px] w-px bg-white/40 z-10 pointer-events-none"
                        />
                        <div className="pl-2">{children}</div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    </div>
);

const extractSameOriginPath = (url) => {
    if (typeof window === "undefined" || !/^https?:\/\//i.test(url || "")) {
        return null;
    }
    try {
        const parsed = new URL(url);
        return parsed.origin === window.location.origin ? parsed.pathname : null;
    } catch {
        return null;
    }
};

const isExternalUrl = (url) =>
    /^https?:\/\//i.test(url || "") && extractSameOriginPath(url) === null;

const urlToNavId = (url) => {
    if (!url || url === "#") return null;
    const samePath = extractSameOriginPath(url);
    if (samePath) return samePath.replace(/^\/+/, "").replace(/\/+$/, "");
    if (isExternalUrl(url)) return null;
    return url.replace(/^\/+/, "").replace(/\/+$/, "");
};

const bySequence = (a, b) => {
    const seqDiff =
        Number(a?.SequenceNo ?? Number.MAX_SAFE_INTEGER) -
        Number(b?.SequenceNo ?? Number.MAX_SAFE_INTEGER);

    if (seqDiff !== 0) return seqDiff;

    const getId = (item) =>
        item?.BaseNavigationId ?? item?.NavigationId ?? item?.SubNavigationId ?? 0;

    return getId(a) - getId(b);
};

const canViewNavigation = (permissionMap, type, id) =>
    permissionMap?.[`${type}:${id}`]?.view === true;

// const transformNavApiResponse = (apiData = [], permissionMap = null, permissionsLoaded = false) => {
//     return [...apiData]
//         .filter((base) => base.IsShowing === true)
//         .sort(bySequence)
//         .map((base) => {
//             const children = [...(base.NavigationDetailsList || [])]
//                 .filter((nav) => nav.IsShowing === true)
//                 .sort(bySequence)
//                 .map((nav) => {
//                     const subChildren = [...(nav.SubNavigationDetailsList || [])]
//                         .filter((sub) => sub.IsShowing === true)
//                         .sort(bySequence)
//                         .filter((sub) => !permissionsLoaded || canViewNavigation(permissionMap, "sub", sub.SubNavigationId))
//                         .map((sub) => ({
//                             key: `sub-${sub.SubNavigationId}`,
//                             navId: urlToNavId(sub.SubNavigationURL),
//                             rawUrl: sub.SubNavigationURL,
//                             externalUrl: isExternalUrl(sub.SubNavigationURL)
//                                 ? sub.SubNavigationURL
//                                 : null,
//                             label: sub.SubNavigationName,
//                             isShowing: sub.IsShowing,
//                         }));

//                     const navVisible = !permissionsLoaded || canViewNavigation(permissionMap, "nav", nav.NavigationId);
//                     if (!navVisible && subChildren.length === 0) return null;

//                     return {
//                         key: `nav-${nav.NavigationId}`,
//                         navId: urlToNavId(nav.NavigationURL),
//                         rawUrl: nav.NavigationURL,
//                         externalUrl: isExternalUrl(nav.NavigationURL)
//                             ? nav.NavigationURL
//                             : null,
//                         label: nav.NavigationName,
//                         isShowing: nav.IsShowing,
//                         children: subChildren,
//                     };
//                 })
//                 .filter(Boolean);

//             const baseVisible = !permissionsLoaded || canViewNavigation(permissionMap, "base", base.BaseNavigationId);
//             if (!baseVisible && children.length === 0) return null;

//             return {
//                 key: `base-${base.BaseNavigationId}`,
//                 navId: urlToNavId(base.BaseNavigationURL),
//                 rawUrl: base.BaseNavigationURL,
//                 label: base.BaseNavigationName,
//                 icon: resolveIcon(base.IconName || base.BaseNavigationIcon),
//                 isShowing: base.IsShowing,
//                 children,
//             };
//         })
//         .filter(Boolean);
// };

//=======================================================================================================================================

const transformNavApiResponse = (apiData = []) => {
    return [...apiData]
        .filter((base) => base.IsShowing === true)
        .sort(bySequence)
        .map((base) => {
            const children = [...(base.NavigationDetailsList || [])]
                .filter((nav) => nav.IsShowing === true)
                .sort(bySequence)
                .map((nav) => {
                    const subChildren = [...(nav.SubNavigationDetailsList || [])]
                        .filter((sub) => sub.IsShowing === true)
                        .sort(bySequence)
                        .map((sub) => ({
                            key: `sub-${sub.SubNavigationId}`,
                            navId: urlToNavId(sub.SubNavigationURL),
                            rawUrl: sub.SubNavigationURL,
                            externalUrl: isExternalUrl(sub.SubNavigationURL)
                                ? sub.SubNavigationURL
                                : null,
                            label: sub.SubNavigationName,
                            isShowing: sub.IsShowing,
                        }));

                    return {
                        key: `nav-${nav.NavigationId}`,
                        navId: urlToNavId(nav.NavigationURL),
                        rawUrl: nav.NavigationURL,
                        externalUrl: isExternalUrl(nav.NavigationURL)
                            ? nav.NavigationURL
                            : null,
                        label: nav.NavigationName,
                        isShowing: nav.IsShowing,
                        children: subChildren,
                    };
                });

            return {
                key: `base-${base.BaseNavigationId}`,
                navId: urlToNavId(base.BaseNavigationURL),
                rawUrl: base.BaseNavigationURL,
                label: base.BaseNavigationName,
                icon: resolveIcon(base.IconName || base.BaseNavigationIcon),
                isShowing: base.IsShowing,
                children,
            };
        });
};

const ONLINE_BOOKING_NAV = {
    key: 'local-air-online-booking',
    navId: 'air/export/online-bookings',
    rawUrl: '/air/export/online-bookings',
    externalUrl: null,
    label: 'Online Booking',
    isShowing: true,
    children: [],
};

const injectLocalAirOnlineBooking = (tree = []) => {
    const already = (list = []) => list.some((item) => item.navId === ONLINE_BOOKING_NAV.navId);
    tree.forEach((base) => {
        const baseLabel = String(base.label || '').toLowerCase();
        const baseUrl = String(base.rawUrl || '');
        if ((baseLabel.includes('air export') || baseUrl.includes('/air/export')) && !already(base.children)) {
            base.children = [...(base.children || []), { ...ONLINE_BOOKING_NAV }];
        }
        (base.children || []).forEach((nav) => {
            const navLabel = String(nav.label || '').toLowerCase();
            const navUrl = String(nav.rawUrl || '');
            if (navLabel === 'export' && baseLabel.includes('air') && !already(nav.children)) {
                nav.children = [...(nav.children || []), { ...ONLINE_BOOKING_NAV, key: 'local-air-online-booking-sub' }];
            }
            if ((navLabel.includes('air export') || navUrl.includes('/air/export')) && !already(base.children)) {
                base.children = [...(base.children || []), { ...ONLINE_BOOKING_NAV }];
            }
        });
    });
    return tree;
};

//=======================================================================================================================================

// const transformNavApiResponse = (apiData = []) => {
//     return [...apiData].sort(bySequence).map((base) => ({
//         key: `base-${base.BaseNavigationId}`,
//         navId: urlToNavId(base.BaseNavigationURL),
//         rawUrl: base.BaseNavigationURL,
//         label: base.BaseNavigationName,
//         icon: resolveIcon(base.IconName || base.BaseNavigationIcon),

//         children: [...(base.NavigationDetailsList || [])]
//             .sort(bySequence)
//             .map((nav) => ({
//                 key: `nav-${nav.NavigationId}`,
//                 navId: urlToNavId(nav.NavigationURL),
//                 rawUrl: nav.NavigationURL,
//                 externalUrl: isExternalUrl(nav.NavigationURL)
//                     ? nav.NavigationURL
//                     : null,
//                 label: nav.NavigationName,

//                 children: [...(nav.SubNavigationDetailsList || [])]
//                     .sort(bySequence)
//                     .map((sub) => ({
//                         key: `sub-${sub.SubNavigationId}`,
//                         navId: urlToNavId(sub.SubNavigationURL),
//                         rawUrl: sub.SubNavigationURL,
//                         externalUrl: isExternalUrl(sub.SubNavigationURL)
//                             ? sub.SubNavigationURL
//                             : null,
//                         label: sub.SubNavigationName,
//                     })),
//             })),
//     }));
// };

const flattenNavIds = (tree) => {
    const ids = [];
    tree.forEach((base) => {
        if (base.navId) ids.push(base.navId);
        base.children.forEach((nav) => {
            if (nav.navId) ids.push(nav.navId);
            nav.children.forEach((sub) => {
                if (sub.navId) ids.push(sub.navId);
            });
        });
    });
    return ids;
};

const findOpenAncestors = (tree, activeNavId) => {
    if (!activeNavId) return { baseKey: null, midKey: null };
    for (const base of tree) {
        if (base.navId === activeNavId) return { baseKey: base.key, midKey: null };
        for (const nav of base.children) {
            if (nav.navId === activeNavId) return { baseKey: base.key, midKey: null };
            if (nav.children.some((s) => s.navId === activeNavId)) {
                return { baseKey: base.key, midKey: nav.key };
            }
        }
    }
    return { baseKey: null, midKey: null };
};

const firstNavIdInBase = (base) => {
    for (const nav of base.children) {
        if (nav.navId) return nav.navId;
        for (const sub of nav.children) {
            if (sub.navId) return sub.navId;
        }
    }
    return base.navId || null;
};

// ─── DynamicSidebarNav — renders the API-driven tree ─────────────────────────
const DynamicSidebarNav = ({ tree, isCollapsed, onNavigate }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const pathname = location.pathname;
    const pressFeedback = useNavPressFeedback();

    const allNavIds = useMemo(() => flattenNavIds(tree), [tree]);
    const activeNavId = useMemo(
        () => resolveActiveNavId(pathname, allNavIds),
        [pathname, allNavIds],
    );

    const [pendingNavId, setPendingNavId] = useState(null);

    useEffect(() => {
        // When navigation completes, remove the temporary highlight
        setPendingNavId(null);
    }, [pathname]);

    const [openBaseIds, setOpenBaseIds] = useState(new Set());
    const [openMidIds, setOpenMidIds] = useState(new Set());

    const toggleBase = (key) =>
        setOpenBaseIds((prev) => (prev.has(key) ? new Set() : new Set([key])));

    const toggleMid = (key) =>
        setOpenMidIds((prev) => (prev.has(key) ? new Set() : new Set([key])));

    const navigateFromBase = (baseKey) => () => {
        setOpenBaseIds(new Set(baseKey ? [baseKey] : []));
        onNavigate?.();
    };

    useEffect(() => {
        const { baseKey, midKey } = findOpenAncestors(tree, activeNavId);
        if (baseKey) setOpenBaseIds(new Set([baseKey]));
        if (midKey) setOpenMidIds(new Set([midKey]));
    }, [tree, activeNavId]);

    const accordionMotion = {
        initial: { height: 0, opacity: 0 },
        animate: { height: "auto", opacity: 1 },
        exit: { height: 0, opacity: 0 },
        transition: { duration: 0.22, ease: [0.4, 0, 0.2, 1] },
        className: "overflow-hidden",
    };

    return (
        <div className="space-y-1">
            {tree.map((base) => {
                // Leaf base item — no NavigationDetailsList
                if (base.children.length === 0) {
                    const external = isExternalUrl(base.rawUrl);
                    if (!external && !base.navId) {
                        return (
                            <DisabledNavItem
                                key={base.key}
                                label={base.label}
                                icon={base.icon}
                                isCollapsed={isCollapsed}
                            />
                        );
                    }
                    return (
                        <NavItem
                            key={base.key}
                            t={t}
                            id={external ? base.key : base.navId}
                            label={base.label}
                            icon={base.icon}
                            externalLink={external ? base.rawUrl : undefined}
                            activeNavId={pendingNavId || activeNavId}
                            setPendingNavId={setPendingNavId}
                            pathname={pathname}
                            isCollapsed={isCollapsed}
                            onNavigate={onNavigate}
                            pressFeedback={pressFeedback}
                        />
                    );
                }

                const isBaseOpen = openBaseIds.has(base.key);
                const isBaseActive =
                    isNavIdActive(pathname, base.navId, activeNavId) ||
                    base.children.some(
                        (nav) =>
                            isNavIdActive(pathname, nav.navId, activeNavId) ||
                            nav.children.some((s) =>
                                isNavIdActive(pathname, s.navId, activeNavId),
                            ),
                    );

                // Hide the outer guide-line whenever at least one TreeParent (mid-level
                // accordion) inside this base section is currently open — the inner
                // TreeParent line at left-[48px] takes over visual duty.
                const anyMidOpen = base.children.some(
                    (nav) => nav.children.length > 0 && openMidIds.has(nav.key),
                );

                return (
                    <div key={base.key} className="mb-1">
                        <ParentNavBtn
                            t={t}
                            icon={base.icon}
                            label={base.label}
                            isActive={isBaseActive}
                            isOpen={isBaseOpen}
                            isCollapsed={isCollapsed}
                            testId={base.key}
                            onClick={() => {
                                if (isCollapsed) {
                                    const landingId = firstNavIdInBase(base);
                                    if (landingId) {
                                        setPendingNavId(landingId);
                                        prefetchRoute(`/${landingId}`);
                                        navigate(`/${landingId}`);
                                    }
                                    onNavigate?.();
                                } else {
                                    toggleBase(base.key);
                                }
                            }}
                        />
                        <AnimatePresence>
                            {isBaseOpen && !isCollapsed && (
                                <motion.div {...accordionMotion}>
                                    {/* Pass hideLine so the outer line disappears while a
                      nested TreeParent accordion is open */}
                                    <TreeMenu showLine hideLine={anyMidOpen}>
                                        {base.children.map((nav) => {
                                            // Flat mid-level item — no SubNavigationDetailsList
                                            if (nav.children.length === 0) {
                                                if (!nav.navId && !nav.externalUrl) {
                                                    return (
                                                        <DisabledLeafItem
                                                            key={nav.key}
                                                            label={nav.label}
                                                        />
                                                    );
                                                }
                                                return (
                                                    <TreeItem
                                                        t={t}
                                                        key={nav.key}
                                                        id={nav.navId}
                                                        label={nav.label}
                                                        externalUrl={nav.externalUrl}
                                                        activeNavId={pendingNavId || activeNavId}
                                                        setPendingNavId={setPendingNavId}
                                                        pathname={pathname}
                                                        onNavigate={navigateFromBase(base.key)}
                                                        pressFeedback={pressFeedback}
                                                    />
                                                );
                                            }

                                            // Nested accordion (e.g. Export / Import)
                                            const isMidOpen = openMidIds.has(nav.key);
                                            const isMidActive = nav.children.some((s) =>
                                                isNavIdActive(pathname, s.navId, activeNavId),
                                            );

                                            return (
                                                <TreeParent
                                                    t={t}
                                                    key={nav.key}
                                                    label={nav.label}
                                                    isActive={isMidActive}
                                                    isOpen={isMidOpen}
                                                    testId={nav.key}
                                                    onClick={() => toggleMid(nav.key)}
                                                >
                                                    {nav.children.map((sub) =>
                                                        sub.navId ? (
                                                            <TreeItem
                                                                t={t}
                                                                key={sub.key}
                                                                id={sub.navId}
                                                                label={sub.label}
                                                                externalUrl={sub.externalUrl}
                                                                activeNavId={pendingNavId || activeNavId}
                                                                setPendingNavId={setPendingNavId}
                                                                pathname={pathname}
                                                                onNavigate={navigateFromBase(base.key)}
                                                                indent="pl-12"
                                                                pressFeedback={pressFeedback}
                                                            />
                                                        ) : (
                                                            <DisabledLeafItem
                                                                key={sub.key}
                                                                label={sub.label}
                                                                indent="pl-12"
                                                            />
                                                        ),
                                                    )}
                                                </TreeParent>
                                            );
                                        })}
                                    </TreeMenu>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                );
            })}
        </div>
    );
};

// ─── Items that stay hand-coded by design ────────────────────────────────────
const StaticExtraNavItems = ({
    position,
    isCollapsed,
    onNavigate,
    pressFeedback,
    activeNavId,
    pathname,
    authUser,
}) => {
    const { t } = useTranslation();
    const cargoRatesExternalLink = useMemo(
        () => buildCargoratesSignInUrl(authUser?.companyId),
        [authUser?.companyId],
    );

    // if (position === "bottom") {
    //   return (
    //     <NavItem
    //       id="api-docs"
    //       label={t("infoxApis")}
    //       icon={Code2}
    //       activeNavId={activeNavId}
    //       pathname={pathname}
    //       isCollapsed={isCollapsed}
    //       onNavigate={onNavigate}
    //       pressFeedback={pressFeedback}
    //       externalLink="https://api.shipez.ai/swagger/index.html"
    //     />
    //   );
    // }

    return (
        <>
            {/* <NavItem
        id="quote-followup"
        label={t("quoteFollowup")}
        icon={MessageSquareMore}
        activeNavId={activeNavId}
        pathname={pathname}
        isCollapsed={isCollapsed}
        onNavigate={onNavigate}
        pressFeedback={pressFeedback}
        externalLink="https://app.mylogpal.ai/SignIn.aspx?b=aXFoYXRYNGJNMUpwdkxkY2xreDZ1dz09&d=dy9XU2RjVGdqNDVxZDFNSmlIbURjQT09"
      />
      <NavItem
        id="api-docs"
        label={t("infoxApis")}
        icon={Code2}
        activeNavId={activeNavId}
        pathname={pathname}
        isCollapsed={isCollapsed}
        onNavigate={onNavigate}
        pressFeedback={pressFeedback}
        externalLink="https://api.shipez.ai/swagger/index.html"
      />
      <NavItem
        id="crm"
        label={t("crm")}
        icon={Users}
        activeNavId={activeNavId}
        pathname={pathname}
        isCollapsed={isCollapsed}
        onNavigate={onNavigate}
        pressFeedback={pressFeedback}
        externalLink="https://crmapp.infoxsystem.com/tms/signin.aspx?b=c2YwRzNabGNVMm9lRzFkb3ZPQm51dz09&d=ZUpLZm02TUdOTkZkTmlyRDRMTnI0dz09"
      /> */}
        </>
    );
};

// ─── NavContent ───────────────────────────────────────────────────────────────
const NavContent = ({
    t,
    isCollapsed,
    onNavigate,
    authUser,
    navApiResponse = [],
    loading,
    navigationLoaded,
}) => {
    const pressFeedback = useNavPressFeedback();
    const location = useLocation();
    const pathname = location.pathname;

    const tree = useMemo(() => {
        const transformedNav = injectLocalAirOnlineBooking(
            transformNavApiResponse(navApiResponse || []),
        );
        console.log('transformedNav: ', transformedNav);
        return transformedNav
    }, [navApiResponse]);

    console.log('tree: ', tree);

    const allNavIds = useMemo(() => flattenNavIds(tree), [tree]);

    const activeNavId = useMemo(
        () => resolveActiveNavId(pathname, allNavIds),
        [pathname, allNavIds],
    );

    if (authUser?.roleID === 1) {
        const superAdminNavItems = [
            {
                id: "admin-dashboard",
                label: "Dashboard",
                icon: LayoutDashboard,
                to: "/admin/",
                activePath: "/admin",
                activeExact: true,
            },
            {
                id: "admin/base_modules",
                label: "Modules",
                icon: Package,
                to: "/admin/base_modules",
            },
            {
                id: "admin/companies",
                label: "Companies",
                icon: Building2,
                to: "/admin/companies",
            },
            { id: "admin/roles", label: "Roles", icon: Shield, to: "/admin/roles" },
            {
                id: "admin/base_navigations",
                label: "Master Navigations",
                icon: Menu,
                to: "/admin/base_navigations",
            },
            {
                id: "admin/master_navigation",
                label: "Navigation Tree",
                icon: Layers3,
                to: "/admin/master_navigation",
            },
            {
                id: "admin/nav_visibility",
                label: "Master Nav Visibility",
                icon: ShieldCheck,
                to: "/admin/nav_visibility",
            },
            {
                id: "admin/company_base_navigations",
                label: "Company Navigations",
                icon: SquareMenu,
                to: "/admin/company_base_navigations",
            },
            {
                id: "admin/company_navigation_tree",
                label: "Company Navigation Tree",
                icon: Layers3,
                to: "/admin/company_navigation_tree",
            },
            {
                id: "admin/company_nav_visibility",
                label: "Company Nav Visibility",
                icon: Compass,
                to: "/admin/company_nav_visibility",
            },
            { id: "admin/users", label: "Users", icon: Users, to: "/admin/users" },
            {
                id: "admin/navigation_order",
                label: "Navigation order",
                icon: List,
                to: "/admin/navigation_order",
            },
        ];

        return (
            <div className="space-y-1">
                {superAdminNavItems.map((item) => (
                    <NavItem
                        t={t}
                        key={item.id}
                        {...item}
                        activeNavId={null}
                        pathname={pathname}
                        isCollapsed={isCollapsed}
                        onNavigate={onNavigate}
                        pressFeedback={pressFeedback}
                    />
                ))}
            </div>
        );
    }

    if (loading || !navigationLoaded) {
        return <SidebarSkeleton isCollapsed={isCollapsed} />;
    }

    return (
        <>
            {/* <div className="space-y-1 pt-1 mb-1">
        <StaticExtraNavItems
          position="top"
          isCollapsed={isCollapsed}
          onNavigate={onNavigate}
          pressFeedback={pressFeedback}
          activeNavId={activeNavId}
          pathname={pathname}
          authUser={authUser}
        />
      </div> */}

            <DynamicSidebarNav
                tree={tree}
                isCollapsed={isCollapsed}
                onNavigate={onNavigate}
            />

            {/* <div className="space-y-1 pt-1">
        <StaticExtraNavItems
          position="bottom"
          isCollapsed={isCollapsed}
          onNavigate={onNavigate}
          pressFeedback={pressFeedback}
          activeNavId={activeNavId}
          pathname={pathname}
          authUser={authUser}
        />
      </div> */}
        </>
    );
};

// ─── Sidebar ──────────────────────────────────────────────────────────────────
const Sidebar = ({ isOpen, onClose }) => {
    const { t } = useTranslation();
    const [isCollapsed, setIsCollapsed] = useState(false);
    const navigate = useNavigate();
    const authUser = useAuthUser();
    // Keep Ocean Import → Export labelling in sync for agent users (desktop + mobile).
    useImportModuleLabels();
    const dispatch = useDispatch();
    const [logoLoaded, setLogoLoaded] = useState(false);

    const navApiResponse = useSelector(
        (state) => state.sidebarNavigation.userNavTree,
    );
    // console.log('navApiResponse: ', navApiResponse);
    const loading = useSelector((state) => state.sidebarNavigation.loading);
    const navigationLoaded = useSelector(
        (state) => state.sidebarNavigation.loaded,
    );

    const masterCompanyDetailsRedux = useSelector(
        (state) => state.common?.MasterCompanyDetails,
    );

    const MasterCompanyDetail = useMemo(() => {
        const row = masterCompanyDetailsRedux?.[0];
        if (row && Object.keys(row).length > 0) return row;
        try {
            const parsed = JSON.parse(
                localStorage.getItem("MasterCompanyDetails") ?? "[]",
            );
            return parsed?.[0] ?? {};
        } catch {
            return {};
        }
    }, [masterCompanyDetailsRedux]);


    const desktopLogoSrc =
        authUser?.roleID === 1
            ? "https://api.shipez.ai/UploadedFile/CompanyProfile/Info-X Software Technology Pvt. Ltd./infoxlogo.png"
            : MasterCompanyDetail?.CompanyLogo ||
            MasterCompanyDetail?.CompanyLogoDark ||
            "";
    const mobileLogoSrc =
        authUser?.roleID === 1
            ? "https://api.shipez.ai/UploadedFile/CompanyProfile/Info-X Software Technology Pvt. Ltd./infoxlogo.png"
            : MasterCompanyDetail?.CompanyLogoMobile ||
            MasterCompanyDetail?.CompanyLogo ||
            "";

    const isMobileView =
        typeof window !== "undefined" && window.innerWidth < 1024;
    const effectiveCollapsed = isMobileView ? false : isCollapsed;

    const handleNavigate = useCallback(() => {
        if (onClose && window.innerWidth < 1024) onClose();
    }, [onClose]);

    const sidebarBase =
        "bg-gradient-to-b from-[#0A0A0A] to-[#121212] border-r border-slate-800 flex flex-col text-[#E8EAED] dark:text-[#9CA3AF]";

    const DesktopFooter = () => (
        <div className="p-3 space-y-2">
            <button
                onClick={() => setIsCollapsed((v) => !v)}
                className="w-full flex items-center justify-center gap-2 h-9 rounded-lg
                   text-[#D1D5DB] hover:text-white hover:bg-white/10
                   dark:text-[#9CA3AF] dark:hover:text-slate-200 dark:hover:bg-white/5
                   border border-white/10 dark:border-white/5 transition-all duration-200"
                title={effectiveCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
                <ChevronLeft
                    className={`w-4 h-4 transition-transform duration-200 ${effectiveCollapsed ? "rotate-180" : ""
                        }`}
                    strokeWidth={2}
                />
                {!effectiveCollapsed && (
                    <span className="text-[12px] font-medium">Collapse</span>
                )}
            </button>
        </div>
    );

    const MobileFooter = () => (
        <div className="p-3 border-t border-slate-800">
            <div className="bg-slate-800/80 rounded-lg px-2 py-2 flex items-center justify-center border border-white/5">
                <span className="font-semibold text-lime-400 text-xs">v1.22.25</span>
            </div>
        </div>
    );
    useEffect(() => {
        if (authUser?.companyId && authUser?.roleID) {
            dispatch(
                getSideBarNavigationList({
                    CompanyId: authUser?.companyId,
                    RoleId: authUser?.roleID,
                }),
                // getSideBarNavigationList({
                //     accountTypeId: authUser?.profileCategory,
                //     roleId: authUser?.roleID,
                // }),
            );
        }
    }, [dispatch, authUser?.companyId, authUser?.roleID]);

    useEffect(() => {
        if (!desktopLogoSrc) return;

        setLogoLoaded(false);

        const image = new Image();

        image.onload = () => setLogoLoaded(true);
        image.onerror = () => setLogoLoaded(true);

        image.src = desktopLogoSrc;
    }, [desktopLogoSrc]);

    return (
        <>
            {/* ── Desktop Sidebar ── */}
            <motion.aside
                initial={{ x: -100, opacity: 0 }}
                animate={{ x: 0, opacity: 1, width: effectiveCollapsed ? 72 : 256 }}
                transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
                className={`hidden lg:flex ${sidebarBase} relative flex-shrink-0 h-screen sticky top-0`}
                style={{ width: effectiveCollapsed ? 72 : 256 }}
                data-testid="sidebar"
            >
                <button
                    type="button"
                    // onClick={() => navigate("/modules")}
                    onClick={() => navigate("/")}
                    aria-label="Open Modules Dashboard"
                    data-testid="sidebar-logo-btn"
                    className="relative p-4 border-b border-slate-800 flex items-center justify-center min-h-[64px] w-full hover:bg-white/[0.03] transition-colors cursor-pointer overflow-hidden"
                >
                    <div
                        className={`relative flex items-center justify-center ${effectiveCollapsed ? "h-8 w-8" : "h-12 w-40"
                            }`}
                    >
                        {!logoLoaded && (
                            <div className="absolute inset-0 animate-pulse rounded-md bg-white/10" />
                        )}

                        {desktopLogoSrc && (
                            <img
                                key={desktopLogoSrc}
                                src={desktopLogoSrc}
                                alt={MasterCompanyDetail?.CompanyName || "Company"}
                                width={effectiveCollapsed ? 32 : 160}
                                height={effectiveCollapsed ? 32 : 48}
                                decoding="async"
                                fetchPriority="high"
                                loading="eager"
                                className={`
          transition-opacity duration-300
          ${effectiveCollapsed
                                        ? "h-8 w-8 object-contain rounded"
                                        : "h-12 w-auto max-w-[160px] object-contain"
                                    }
          ${logoLoaded ? "opacity-100" : "opacity-0"}
        `}
                            />
                        )}
                    </div>
                </button>

                <nav
                    className={`sidebar-nav-scroll flex-1 min-h-0 ${effectiveCollapsed ? "p-2" : "p-3"
                        } space-y-0.5 overflow-y-auto overflow-x-hidden`}
                    data-testid="sidebar-nav"
                >
                    <NavContent
                        t={t}
                        isCollapsed={effectiveCollapsed}
                        onNavigate={handleNavigate}
                        authUser={authUser}
                        navApiResponse={navApiResponse}
                        loading={loading}
                        navigationLoaded={navigationLoaded}
                    />
                </nav>

                <DesktopFooter />
            </motion.aside>

            {/* ── Mobile Sidebar (drawer) ── */}
            <AnimatePresence>
                {isOpen && (
                    <motion.aside
                        initial={{ x: "-100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "-100%" }}
                        transition={{ type: "spring", damping: 25, stiffness: 200 }}
                        className={`fixed inset-y-0 left-0 z-50 w-[280px] sm:w-[300px] ${sidebarBase} lg:hidden shadow-2xl`}
                        data-testid="mobile-sidebar"
                    >
                        <div className="p-4 border-b border-slate-800 flex items-center justify-between">
                            <button
                                type="button"
                                onClick={() => {
                                    // navigate("/modules");
                                    navigate("/");
                                    onClose && onClose();
                                }}
                                aria-label="Open Modules Dashboard"
                                data-testid="sidebar-logo-btn-mobile"
                                className="flex-shrink-0 cursor-pointer"
                            >
                                <img
                                    src={mobileLogoSrc}
                                    alt={MasterCompanyDetail?.CompanyName || "Company"}
                                    className="h-10 w-auto object-contain"
                                />
                            </button>
                            <button
                                onClick={onClose}
                                className="p-1.5 rounded-lg hover:bg-white/5 transition-colors"
                                aria-label="Close menu"
                            >
                                <X className="w-5 h-5 text-[#E8EAED] dark:text-[#9CA3AF]" />
                            </button>
                        </div>

                        <nav
                            className="sidebar-nav-scroll flex-1 min-h-0 p-3 space-y-0.5 overflow-y-auto"
                            data-testid="mobile-sidebar-nav"
                        >
                            <NavContent
                                t={t}
                                isCollapsed={false}
                                onNavigate={handleNavigate}
                                authUser={authUser}
                                navApiResponse={navApiResponse}
                                loading={loading}
                                navigationLoaded={navigationLoaded}
                            />
                        </nav>

                        <MobileFooter />
                    </motion.aside>
                )}
            </AnimatePresence>
        </>
    );
};

export default Sidebar;
